import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { type BreadcrumbSegment } from '../../state/breadcrumb-state';

/**
 * Breadcrumb navigation component.
 * Renders a <nav aria-label="Breadcrumb"> with an <ol> of <li> items.
 * Final segment is non-interactive text with aria-current="page".
 * Preceding segments are rendered as <Link> elements for SPA navigation.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BreadcrumbProps {
  readonly segments: readonly BreadcrumbSegment[];
  readonly className?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function Breadcrumb({ segments, className }: BreadcrumbProps): React.JSX.Element | null {
  if (segments.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className={cn('text-sm', className)}>
      <ol className="flex items-center gap-1.5">
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;

          return (
            <li key={`${segment.label}-${index}`} className="flex items-center gap-1.5">
              {index > 0 && (
                <span className="text-muted-foreground" aria-hidden="true">
                  /
                </span>
              )}
              {isLast || !segment.href ? (
                <span
                  className="text-foreground font-medium"
                  aria-current="page"
                >
                  {segment.label}
                </span>
              ) : (
                <Link
                  to={segment.href}
                  className="text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                >
                  {segment.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
